import React, { useState, useEffect } from 'react';
import {
  Users,
  UserPlus,
  Search,
  Edit,
  Trash2,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  ChevronUp,
  ChevronDown,
  Key,
  Upload,
  RefreshCw
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import { FirmAdminLayout } from './FirmAdminLayout';
import { AddUserModal, EditUserModal, DeleteUserModal, ChangePasswordModal, UserImportModal, UserImportResultsModal } from './UserModals';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  status: 'active' | 'invited' | 'disabled';
  createdAt: string;
  lastLoginAt?: string;
}



interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  password?: string;
  sendInvitation?: boolean;
}

interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  roles?: string[];
  status?: 'active' | 'invited' | 'disabled';
}



const SortableHeader: React.FC<{
  label: string;
  field: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
}> = ({ label, field, sortBy, sortOrder, onSort }) => {
  const isActive = sortBy === field;

  return (
    <button
      onClick={() => onSort(field)}
      className="flex items-center space-x-1 text-left font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
    >
      <span>{label}</span>
      <div className="flex flex-col">
        <ChevronUp
          className={`w-3 h-3 ${isActive && sortOrder === 'asc' ? 'text-primary-500' : 'text-gray-300'}`}
        />
        <ChevronDown
          className={`w-3 h-3 -mt-1 ${isActive && sortOrder === 'desc' ? 'text-primary-500' : 'text-gray-300'}`}
        />
      </div>
    </button>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' };
      case 'invited':
        return { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Invited' };
      case 'disabled':
        return { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Disabled' };
      default:
        return { color: 'bg-gray-100 text-gray-800', icon: Clock, label: status };
    }
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </span>
  );
};

const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'FirmAdmin':
        return 'bg-purple-100 text-purple-800';
      case 'Manager':
        return 'bg-blue-100 text-blue-800';
      case 'Staff':
        return 'bg-green-100 text-green-800';
      case 'Client':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getRoleColor(role)}`}>
      {role === 'FirmAdmin' && <Shield className="w-3 h-3 mr-1" />}
      {role}
    </span>
  );
};

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportResultsModal, setShowImportResultsModal] = useState(false);
  const [importResults, setImportResults] = useState<{
    successful: any[];
    failed: Array<{ user: CreateUserData; error: string }>;
    total: number;
  } | null>(null);
  const [sortBy, setSortBy] = useState('firstName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, statusFilter, roleFilter, sortBy, sortOrder]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(roleFilter && { role: roleFilter }),
        sortBy,
        sortOrder,
      });

      const response = await api.get(`/firm-admin/users?${params}`);
      
      if (response.data.success) {
        setUsers(response.data.data.users);
        setPagination(response.data.data.pagination);
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData: CreateUserData) => {
    try {
      setActionLoading(true);
      const response = await api.post('/firm-admin/users', userData);

      if (response.data.success) {
        toast.success('User created successfully');
        setShowAddUserModal(false);
        fetchUsers();
      } else {
        throw new Error('Failed to create user');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateUser = async (userId: string, userData: UpdateUserData) => {
    try {
      setActionLoading(true);
      const response = await api.patch(`/firm-admin/users/${userId}`, userData);

      if (response.data.success) {
        toast.success('User updated successfully');
        setShowEditUserModal(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        throw new Error('Failed to update user');
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setActionLoading(true);
      const response = await api.delete(`/firm-admin/users/${userId}`);

      if (response.data.success) {
        toast.success('User deleted successfully');
        setShowDeleteModal(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        throw new Error('Failed to delete user');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePassword = (user: User) => {
    setSelectedUser(user);
    setShowChangePasswordModal(true);
  };

  const handleResendInvitation = async (userId: string) => {
    try {
      setActionLoading(true);
      const response = await api.post('/firm-admin/users/bulk-action', {
        userIds: [userId],
        action: 'resend_invitation'
      });

      if (response.data.success) {
        toast.success('Invitation sent successfully');
        fetchUsers();
      } else {
        throw new Error('Failed to resend invitation');
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      toast.error(error.response?.data?.message || 'Failed to resend invitation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    const action = newStatus === 'active' ? 'activate' : 'deactivate';

    try {
      setActionLoading(true);
      const response = await api.post('/firm-admin/users/bulk-action', {
        userIds: [userId],
        action
      });

      if (response.data.success) {
        toast.success(`User ${action}d successfully`);
        fetchUsers();
      } else {
        throw new Error(`Failed to ${action} user`);
      }
    } catch (error: any) {
      console.error(`Error ${action}ing user:`, error);
      toast.error(error.response?.data?.message || `Failed to ${action} user`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleImportUsers = async (users: CreateUserData[]) => {
    try {
      setActionLoading(true);
      const successful: any[] = [];
      const failed: Array<{ user: CreateUserData; error: string }> = [];

      // Close import modal and show results modal with progress
      setShowImportModal(false);
      setImportResults({ 
        successful: [], 
        failed: [], 
        total: users.length,
        isImporting: true,
        currentProgress: 0
      });
      setShowImportResultsModal(true);

      // Process users in batches to avoid timeout
      const BATCH_SIZE = 5;
      const batches = [];
      for (let i = 0; i < users.length; i += BATCH_SIZE) {
        batches.push(users.slice(i, i + BATCH_SIZE));
      }

      // Process each batch sequentially
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        // Update progress in modal
        const progress = Math.round(((batchIndex * BATCH_SIZE) / users.length) * 100);
        setImportResults({ 
          successful: [...successful], 
          failed: [...failed], 
          total: users.length,
          isImporting: true,
          currentProgress: progress
        });

        const results = await Promise.allSettled(
          batch.map(user => 
            api.post('/firm-admin/users', user, {
              timeout: 30000 // 30 second timeout per request
            })
          )
        );

        results.forEach((result, index) => {
          const userIndex = batchIndex * BATCH_SIZE + index;
          if (result.status === 'fulfilled') {
            successful.push(result.value);
          } else {
            // Extract user-friendly error message
            let errorMessage = 'Unknown error';
            const error = result.reason;

            // Try to extract error message from different response formats
            if (error?.response?.data?.error?.message) {
              errorMessage = error.response.data.error.message;
            } else if (error?.response?.data?.message) {
              errorMessage = error.response.data.message;
            } else if (error?.response?.data?.error) {
              errorMessage = typeof error.response.data.error === 'string'
                ? error.response.data.error
                : 'Unknown error';
            } else if (error?.response?.status) {
              const statusCode = error.response.status;
              switch (statusCode) {
                case 409:
                  errorMessage = 'User already exists (duplicate email)';
                  break;
                case 400:
                  errorMessage = 'Invalid data provided';
                  break;
                case 422:
                  errorMessage = 'Missing required information';
                  break;
                case 500:
                  errorMessage = 'Server error occurred';
                  break;
                default:
                  errorMessage = `Request failed (Error ${statusCode})`;
              }
            } else if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
              errorMessage = 'Request timeout - user may have been created';
            } else if (error?.message) {
              errorMessage = error.message;
            }

            failed.push({
              user: users[userIndex],
              error: errorMessage,
            });
          }
        });

        // Update progress after each batch
        setImportResults({ 
          successful: [...successful], 
          failed: [...failed], 
          total: users.length,
          isImporting: true,
          currentProgress: Math.min(100, Math.round((((batchIndex + 1) * BATCH_SIZE) / users.length) * 100))
        });
      }

      // Final update - import complete
      setImportResults({ 
        successful, 
        failed, 
        total: users.length,
        isImporting: false,
        currentProgress: 100
      });
      
      // Refresh user list after import
      await fetchUsers();
      
      // Show summary toast
      if (failed.length === 0) {
        toast.success(`Successfully imported all ${successful.length} users`);
      } else if (successful.length === 0) {
        toast.error('Failed to import all users');
      } else {
        toast.success(`Imported ${successful.length} of ${users.length} users`);
      }
    } catch (error: any) {
      console.error('Error importing users:', error);
      toast.error('Failed to import users');
      setImportResults(prev => prev ? { ...prev, isImporting: false } : null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users first');
      return;
    }

    // Confirmation dialog for destructive actions
    if (action === 'delete') {
      const confirmed = window.confirm(
        `Are you sure you want to delete ${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''}? This action cannot be undone.`
      );
      if (!confirmed) return;
    } else if (action === 'deactivate') {
      const confirmed = window.confirm(
        `Are you sure you want to deactivate ${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''}?`
      );
      if (!confirmed) return;
    }

    try {
      setActionLoading(true);
      const response = await api.post('/firm-admin/users/bulk-action', {
        userIds: selectedUsers,
        action,
      });

      if (response.data.success) {
        const actionMessages = {
          activate: 'activated',
          deactivate: 'deactivated',
          delete: 'deleted',
          resend_invitation: 'sent invitations to'
        };

        const message = actionMessages[action as keyof typeof actionMessages] || action;
        toast.success(`Successfully ${message} ${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''}`);
        setSelectedUsers([]);
        fetchUsers();
      } else {
        throw new Error('Failed to perform bulk action');
      }
    } catch (error: any) {
      console.error('Error performing bulk action:', error);
      toast.error(error.response?.data?.message || 'Failed to perform bulk action');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  return (
    <FirmAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">User Management</h1>
              <p className="text-gray-600">Manage your firm's team members</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center px-4 py-2 bg-white text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </button>
              <button
                onClick={() => setShowAddUserModal(true)}
                className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </button>
            </div>
          </div>
        </div>
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="disabled">Disabled</option>
            </select>
            
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Roles</option>
              <option value="Manager">Manager</option>
              <option value="Staff">Staff</option>
              <option value="Client">Client</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleBulkAction('activate')}
                  disabled={actionLoading}
                  className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {actionLoading ? (
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                  ) : (
                    <CheckCircle className="w-3 h-3 mr-1" />
                  )}
                  Activate
                </button>
                <button
                  onClick={() => handleBulkAction('deactivate')}
                  disabled={actionLoading}
                  className="px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {actionLoading ? (
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                  ) : (
                    <XCircle className="w-3 h-3 mr-1" />
                  )}
                  Deactivate
                </button>
                <button
                  onClick={() => handleBulkAction('resend_invitation')}
                  disabled={actionLoading}
                  className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {actionLoading ? (
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                  ) : (
                    <Mail className="w-3 h-3 mr-1" />
                  )}
                  Resend Invitation
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  disabled={actionLoading}
                  className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {actionLoading ? (
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                  ) : (
                    <Trash2 className="w-3 h-3 mr-1" />
                  )}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs">
                    <SortableHeader
                      label="User"
                      field="firstName"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs">
                    <SortableHeader
                      label="Role"
                      field="roles"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs">
                    <SortableHeader
                      label="Status"
                      field="status"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs">
                    <SortableHeader
                      label="Last Login"
                      field="lastLoginAt"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
                      <p className="text-gray-500 mt-2">Loading users...</p>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No users found</p>
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-600 font-medium">
                              {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {user.roles.map((role) => (
                            <RoleBadge key={role} role={role} />
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={user.status} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {user.lastLoginAt 
                          ? new Date(user.lastLoginAt).toLocaleDateString()
                          : 'Never'
                        }
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowEditUserModal(true);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Edit user"
                            disabled={actionLoading}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {user.status === 'invited' && (
                            <button
                              onClick={() => handleResendInvitation(user.id)}
                              className="p-1 text-gray-400 hover:text-blue-600"
                              title="Resend invitation"
                              disabled={actionLoading}
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleChangePassword(user)}
                            className="p-1 text-gray-400 hover:text-yellow-600"
                            title="Change password"
                            disabled={actionLoading}
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleUserStatus(user.id, user.status)}
                            className={`p-1 text-gray-400 hover:${user.status === 'active' ? 'text-red-600' : 'text-green-600'}`}
                            title={user.status === 'active' ? 'Deactivate user' : 'Activate user'}
                            disabled={actionLoading}
                          >
                            {user.status === 'active' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDeleteModal(true);
                            }}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete user"
                            disabled={actionLoading}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>

                  {/* Page numbers */}
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(pagination.pages - 4, pagination.page - 2)) + i;
                      if (pageNum > pagination.pages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 text-sm rounded ${
                            pageNum === pagination.page
                              ? 'bg-primary-500 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.pages))}
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onSubmit={handleCreateUser}
        loading={actionLoading}
      />

      <EditUserModal
        isOpen={showEditUserModal}
        onClose={() => {
          setShowEditUserModal(false);
          setSelectedUser(null);
        }}
        onSubmit={handleUpdateUser}
        user={selectedUser}
        loading={actionLoading}
      />

      <DeleteUserModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteUser}
        user={selectedUser}
        loading={actionLoading}
      />

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => {
          setShowChangePasswordModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />

      <UserImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportUsers}
        isLoading={actionLoading}
      />

      <UserImportResultsModal
        isOpen={showImportResultsModal}
        onClose={() => setShowImportResultsModal(false)}
        results={importResults}
      />
    </FirmAdminLayout>
  );
};
